import Close from '@mui/icons-material/Close'
import CloudUpload from '@mui/icons-material/CloudUpload'
import PhotoLibrary from '@mui/icons-material/PhotoLibrary'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Visibility from '@mui/icons-material/Visibility'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { default as ErrorIcon } from '@mui/icons-material/Error'
import { default as DescriptionIcon } from '@mui/icons-material/Description'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tab,
  Tabs,
  Typography,
  Chip,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import SendPrescriptionImages from 'src/clinic/components/ClinicManageBookingsPage/sendPrescriptionData/components/SendPrescriptionImages'
import { getDataWithParams, postDataWithParams } from 'src/consumer/services/profileService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import PastPatientReportsDialog from 'src/clinic/components/ClinicManageBookingsPage/pastPatientReports/PastPatientReportsDialog'
import PdfAwareImage from 'src/clinic/components/ClinicManageBookingsPage/components/PdfAwareImage'
import { getPrescriptionApiSignatures } from 'src/shared/utils/calendarReportApiHelper'
import styled from 'styled-components'

// Styled Components
const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 20px;
    overflow: hidden;
    max-width: 900px;
    width: 100%;
  }
`

const DialogHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(67, 56, 202, 0.2);
`

const DialogTitleText = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  gap: 12px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const DialogContentContainer = styled(Box)`
  padding: 0;
  background: linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
  min-height: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`

const StyledTabs = styled(Tabs)`
  border-bottom: 2px solid rgba(67, 56, 202, 0.1);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  
  .MuiTab-root {
    text-transform: none;
    font-weight: 600;
    font-size: 15px;
    min-height: 56px;
    color: rgba(67, 56, 202, 0.7);
    transition: all 0.2s ease;
    
    &:hover {
      color: rgba(67, 56, 202, 0.9);
      background: rgba(67, 56, 202, 0.05);
    }
    
    &.Mui-selected {
      color: rgba(67, 56, 202, 1);
      font-weight: 700;
    }
  }
  
  .MuiTabs-indicator {
    background: linear-gradient(90deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%);
    height: 3px;
    border-radius: 3px 3px 0 0;
  }
`

const TabPanel = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(67, 56, 202, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(67, 56, 202, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(67, 56, 202, 0.3);
    }
  }
`

const FooterActions = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(67, 56, 202, 0.1);
  gap: 12px;
`

const SaveButton = styled(Button)`
  background: #4338ca !important;
  color: #ffffff !important;
  font-weight: 700;
  text-transform: none;
  padding: 12px 32px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(67, 56, 202, 0.4);
  transition: all 0.2s ease;
  min-width: 160px;
  min-height: 48px;
  font-size: 15px;
  letter-spacing: 0.3px;
  border: 2px solid #4338ca;
  
  &:hover {
    box-shadow: 0 6px 24px rgba(67, 56, 202, 0.5);
    transform: translateY(-2px);
    background: #5b52d3 !important;
    border-color: #5b52d3;
  }
  
  &:active {
    transform: translateY(0);
    background: #3730a3 !important;
    border-color: #3730a3;
  }
  
  &:disabled {
    background: #9ca3af !important;
    color: #ffffff !important;
    border-color: #9ca3af;
    box-shadow: none;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    padding: 14px 24px;
    min-height: 52px;
    font-size: 16px;
    font-weight: 700;
    background: #4338ca !important;
    color: #ffffff !important;
  }
`

const CancelButton = styled(Button)`
  color: rgba(67, 56, 202, 0.7);
  font-weight: 600;
  text-transform: none;
  padding: 10px 24px;
  border-radius: 10px;
  
  &:hover {
    background: rgba(67, 56, 202, 0.05);
  }
`

const ImageCountChip = styled(Chip)`
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
  color: rgba(67, 56, 202, 0.9);
  font-weight: 600;
  border: 1px solid rgba(67, 56, 202, 0.2);
`

const ErrorAlert = styled(Alert)`
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);
`

export default function SendPrescriptionImagesDialog({
  bookedEventInfo,
  closePopover,
  showGallery,
  mentorUserId,
  galleryImagesSearch,
  fileUploadApiSignature,
  prescriptionImagesGetApiSignature,
  prescriptionImagesSaveApiSignature,
  showPatientHistoryInUploadPrescriptionWidget,
  patientHistoryGetApiSignature,
  open,
}) {
  // Get default prescription API signatures if not provided (portal-aware)
  const defaultPrescriptionApis = getPrescriptionApiSignatures()
  const finalGetApi = prescriptionImagesGetApiSignature || defaultPrescriptionApis.prescriptionImagesGetApiSignature
  const finalSaveApi = prescriptionImagesSaveApiSignature || defaultPrescriptionApis.prescriptionImagesSaveApiSignature

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [prescriptionImages, setPrescriptionImages] = useState([])
  const [tabValue, setTabValue] = useState(0)
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false)
  const [previewImageIndex, setPreviewImageIndex] = useState(0)
  const [previewViewMode, setPreviewViewMode] = useState('horizontal') // 'horizontal' or 'vertical'

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const isOpen = open !== undefined ? open : true

  // Helper function to get image URL from image ID or object
  const getImageUrl = (image) => {
    if (typeof image === 'string') {
      return `https://media.aumhum.com/images/prod/${image}`
    }
    if (image?.fileId) {
      return `https://media.aumhum.com/images/prod/${image.fileId}`
    }
    if (image?.id) {
      return `https://media.aumhum.com/images/prod/${image.id}`
    }
    if (image?.signedUrl) {
      return image.signedUrl.startsWith('http') ? image.signedUrl : `https://media.aumhum.com/images/prod/${image.signedUrl}`
    }
    return null
  }

  // Get all image URLs for preview
  const getPreviewImages = () => {
    return prescriptionImages
      .map((img, index) => ({
        url: getImageUrl(img),
        id: typeof img === 'string' ? img : img?.fileId || img?.id || index,
        index,
      }))
      .filter((img) => img.url)
  }

  useEffect(() => {
    if (isOpen && bookedEventInfo?.id) {
      fetchPrescriptionImages()
    } else if (!isOpen) {
      // Reset state when dialog closes
      setPrescriptionImages([])
      setError(null)
      setSuccessMessage(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bookedEventInfo?.id])

  const fetchPrescriptionImages = async () => {
    if (!bookedEventInfo?.id) {
      setError('Invalid booking information')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const resp = await getDataWithParams(finalGetApi, {
        eventId: bookedEventInfo?.id,
      })
      
      if (resp.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          const respData = getDataMapFromCommonResponse(resp)
          setPrescriptionImages(respData?.prescriptionImages || [])
        } else {
          setError('Failed to load existing prescription images')
        }
      } else {
        setError('Network error. Please check your connection and try again.')
      }
    } catch (err) {
      console.error('Error fetching prescription images:', err)
      setError('An unexpected error occurred while loading images')
    } finally {
      setLoading(false)
    }
  }

  const savePrescription = async () => {
    if (!bookedEventInfo?.id) {
      setError('Invalid booking information')
      return
    }

    if (!prescriptionImages || prescriptionImages.length === 0) {
      setError('Please add at least one image before saving')
      return
    }

    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const resp = await postDataWithParams(finalSaveApi, prescriptionImages, {
        eventId: bookedEventInfo?.id,
      })
      
      if (resp.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          setSuccessMessage('Prescription images uploaded successfully!')
          toast.success('Prescription images uploaded successfully')
          setTimeout(() => {
            closePopover()
          }, 1500)
        } else {
          const errorMsg = 'Failed to save prescription images'
          setError(errorMsg)
          toast.error(errorMsg)
        }
      } else {
        const errorMsg = 'Network error. Please check your connection and try again.'
        setError(errorMsg)
        toast.error(errorMsg)
      }
    } catch (err) {
      console.error('Error saving prescription images:', err)
      const errorMsg = err?.message || 'Network error. Please check your connection and try again.'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }


  const hasImages = prescriptionImages && prescriptionImages.length > 0

  return (
    <>
      <StyledDialog
        open={isOpen}
        onClose={closePopover}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogHeader>
          <DialogTitleText>
            <DescriptionIcon sx={{ fontSize: 24 }} />
            Upload Prescription Images
            {bookedEventInfo?.userName && (
              <Typography component="span" sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.9 }}>
                for {bookedEventInfo.userName}
              </Typography>
            )}
          </DialogTitleText>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {hasImages && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={() => setPreviewDialogOpen(true)}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '6px 12px', sm: '8px 16px' },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderColor: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                >
                  View
                </Button>
                <ImageCountChip
                  label={`${prescriptionImages.length} ${prescriptionImages.length === 1 ? 'Image' : 'Images'}`}
                  size="small"
                />
              </>
            )}
            <IconButton
              onClick={closePopover}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogHeader>

        <DialogContentContainer>
          {error && (
            <Box sx={{ p: 2 }}>
              <ErrorAlert severity="error" icon={<ErrorIcon />}>
                {error}
              </ErrorAlert>
            </Box>
          )}

          {showGallery ? (
            <>
              <StyledTabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                variant="fullWidth"
              >
                <Tab
                  icon={<CloudUpload sx={{ fontSize: 20, mb: 0.5 }} />}
                  iconPosition="start"
                  label="Upload New"
                />
                <Tab
                  icon={<PhotoLibrary sx={{ fontSize: 20, mb: 0.5 }} />}
                  iconPosition="start"
                  label="Choose from Gallery"
                />
              </StyledTabs>

              <TabPanel role="tabpanel" hidden={tabValue !== 0}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress size={40} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                  </Box>
                ) : (
                  <Box>
                    <SendPrescriptionImages
                      bookedEventInfo={bookedEventInfo}
                      setUploadPrescriptionImages={setPrescriptionImages}
                      uploadPrescriptionImages={prescriptionImages}
                      loading={loading}
                      setLoading={setLoading}
                      showGallery={false}
                      mentorUserId={mentorUserId}
                      galleryImagesSearch={galleryImagesSearch}
                      fileUploadApiSignature={fileUploadApiSignature}
                    />
                  </Box>
                )}
              </TabPanel>

              <TabPanel role="tabpanel" hidden={tabValue !== 1}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress size={40} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                  </Box>
                ) : (
                  <SendPrescriptionImages
                    bookedEventInfo={bookedEventInfo}
                    setUploadPrescriptionImages={setPrescriptionImages}
                    uploadPrescriptionImages={prescriptionImages}
                    loading={loading}
                    setLoading={setLoading}
                    showGallery={true}
                    mentorUserId={mentorUserId}
                    galleryImagesSearch={galleryImagesSearch}
                    fileUploadApiSignature={fileUploadApiSignature}
                  />
                )}
              </TabPanel>
            </>
          ) : (
            <TabPanel>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                  <CircularProgress size={40} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                </Box>
              ) : (
                <SendPrescriptionImages
                  bookedEventInfo={bookedEventInfo}
                  setUploadPrescriptionImages={setPrescriptionImages}
                  uploadPrescriptionImages={prescriptionImages}
                  loading={loading}
                  setLoading={setLoading}
                  showGallery={false}
                  mentorUserId={mentorUserId}
                  galleryImagesSearch={galleryImagesSearch}
                  fileUploadApiSignature={fileUploadApiSignature}
                />
              )}
            </TabPanel>
          )}

          {showPatientHistoryInUploadPrescriptionWidget && (
            <Box
              sx={{
                borderTop: '1px solid rgba(67, 56, 202, 0.1)',
                maxHeight: '400px',
                overflowY: 'auto',
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'rgba(67, 56, 202, 0.8)', fontWeight: 600 }}>
                Patient History
              </Typography>
              <PastPatientReportsDialog
                patientInfo={{
                  ...(bookedEventInfo?.patientInfo || {}),
                  id: bookedEventInfo?.patientId,
                }}
                forceShowAllReports
                patientHistoryGetApiSignature={patientHistoryGetApiSignature}
              />
            </Box>
          )}
        </DialogContentContainer>

        <FooterActions
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <CancelButton
            onClick={closePopover}
            disabled={saving}
            fullWidth={isMobile}
            sx={{
              order: { xs: 2, sm: 0 },
            }}
          >
            Cancel
          </CancelButton>
          <Box sx={{ flex: { xs: 0, sm: 1 } }} />
          <SaveButton
            onClick={savePrescription}
            disabled={loading || saving || !hasImages}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
            fullWidth={isMobile}
            sx={{
              order: { xs: 1, sm: 0 },
            }}
          >
            {saving ? 'Saving...' : hasImages ? `Save ${prescriptionImages.length} ${prescriptionImages.length === 1 ? 'Image' : 'Images'}` : 'No Images to Save'}
          </SaveButton>
        </FooterActions>
      </StyledDialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false)
          setPreviewImageIndex(0)
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            '@media (max-width: 768px)': {
              margin: '8px',
              maxHeight: 'calc(100vh - 16px)',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Preview Images ({getPreviewImages().length})
            </Typography>
            <Button
              size="small"
              variant={previewViewMode === 'horizontal' ? 'contained' : 'outlined'}
              onClick={() => setPreviewViewMode('horizontal')}
              sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5 }}
            >
              Horizontal
            </Button>
            <Button
              size="small"
              variant={previewViewMode === 'vertical' ? 'contained' : 'outlined'}
              onClick={() => setPreviewViewMode('vertical')}
              sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5 }}
            >
              Vertical
            </Button>
          </Box>
          <IconButton onClick={() => {
            setPreviewDialogOpen(false)
            setPreviewImageIndex(0)
          }}>
            <Close />
          </IconButton>
        </Box>
        
        {previewViewMode === 'horizontal' ? (
          // Horizontal Scroll View with Navigation
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* Previous Button */}
            {getPreviewImages().length > 1 && (
              <IconButton
                onClick={() => setPreviewImageIndex((prev) => Math.max(0, prev - 1))}
                disabled={previewImageIndex === 0}
                sx={{
                  position: 'absolute',
                  left: 8,
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                  },
                }}
              >
                <ChevronLeft />
              </IconButton>
            )}

            {/* Image Container */}
            <DialogContent
              sx={{
                p: 0,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                minHeight: { xs: '400px', sm: '500px' },
              }}
            >
              {getPreviewImages().length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body1">No images to preview</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    p: { xs: 2, sm: 3 },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                      Image {previewImageIndex + 1} of {getPreviewImages().length}
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        p: { xs: 1, sm: 2 },
                        minHeight: { xs: '300px', sm: '400px' },
                      }}
                    >
                      <PdfAwareImage
                        src={getPreviewImages()[previewImageIndex]?.url}
                        alt={`Prescription ${previewImageIndex + 1}`}
                        variant="panel"
                        imgStyle={{
                          maxWidth: '100%',
                          maxHeight: '70vh',
                          height: 'auto',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          display: 'block',
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>

            {/* Next Button */}
            {getPreviewImages().length > 1 && (
              <IconButton
                onClick={() => setPreviewImageIndex((prev) => Math.min(getPreviewImages().length - 1, prev + 1))}
                disabled={previewImageIndex === getPreviewImages().length - 1}
                sx={{
                  position: 'absolute',
                  right: 8,
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                  },
                }}
              >
                <ChevronRight />
              </IconButton>
            )}
          </Box>
        ) : (
          // Vertical Stack View (A4 style)
          <DialogContent
            sx={{
              p: { xs: 1, sm: 2 },
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.5, sm: 2 },
              minHeight: 0,
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0, 0, 0, 0.05)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.3)',
                },
              },
            }}
          >
            {getPreviewImages().length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200,
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body1">No images to preview</Typography>
              </Box>
            ) : (
              getPreviewImages().map((img, index) => (
                <Box
                  key={img.id || index}
                  sx={{
                    width: '100%',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: 'rgba(67, 56, 202, 0.05)',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Image {index + 1} of {getPreviewImages().length}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: '#f8f9fa',
                      p: { xs: 1, sm: 2 },
                      minHeight: { xs: '200px', sm: '300px' },
                    }}
                  >
                    <PdfAwareImage
                      src={img.url}
                      alt={`Prescription ${index + 1}`}
                      variant="panel"
                      imgStyle={{
                        width: '100%',
                        maxWidth: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        display: 'block',
                      }}
                    />
                  </Box>
                </Box>
              ))
            )}
          </DialogContent>
        )}
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
